import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { FaClock, FaCar, FaUserAlt, FaInfoCircle, FaTools, FaDollarSign, FaWrench, FaCog, FaUserTie } from 'react-icons/fa';
import FinalizarMantenimientoModal from './FinalizarMantenimientoModal';
import FotosAntesModal from './FotosAntesModal';
import MantenimientoDetailModal from '@/components/mantenimientos/MantenimientoDetailModal';
import AppointmentContextMenu from './AppointmentContextMenu';
import Badge from '@/components/common/Badge';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import * as citasApiService from '@/services/citasApiService';
import toastService from '@/utils/toastService';
import { getColorEstadoCita, ESTADOS_CITAS } from '@/constants/estados';

/**
 * Componente de lista de citas del día
 */
const AppointmentList = ({ 
  selectedDate, 
  citas, 
  vehiculos, 
  clientes, 
  isAdmin, 
  isSuperAdmin,
  clienteData, 
  onEditCita,
  onDeleteCita,
  onRefreshCitas
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [finalizarModalOpen, setFinalizarModalOpen] = useState(false);
  const [citaToFinalize, setCitaToFinalize] = useState(null);
  const [mantenimientosData, setMantenimientosData] = useState({});
  const [mantenimientoDetailOpen, setMantenimientoDetailOpen] = useState(false);
  const [mantenimientoSeleccionado, setMantenimientoSeleccionado] = useState(null);
  const [fotosAntesModalOpen, setFotosAntesModalOpen] = useState(false);
  const [fotosAntesCita, setFotosAntesCita] = useState(null);
  const cargandoMantenimientos = useRef(new Set());
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    // Limpiar al desmontar
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cargar datos de mantenimiento para citas completadas con cache optimizado
  useEffect(() => {
    const cargarMantenimientos = async () => {
      if (!citas || citas.length === 0) return;
      
      // Crear array de IDs de citas completadas que necesitan mantenimiento
      const citasCompletadas = citas
        .filter(cita => cita.Estado === ESTADOS_CITAS.COMPLETADA.nombre && cita.finalizada)
        .map(cita => cita.id || cita.ID)
        .filter(Boolean);
      
      // Solo cargar mantenimientos para citas que no están ya en cache y no se están cargando
      const citasSinCache = citasCompletadas.filter(citaId => 
        !mantenimientosData[citaId] && !cargandoMantenimientos.current.has(citaId)
      );
      
      if (citasSinCache.length === 0) return;
      
      // Marcar como "cargando" para evitar solicitudes duplicadas
      citasSinCache.forEach(citaId => cargandoMantenimientos.current.add(citaId));
      
      // Cargar solo los mantenimientos que no están en cache
      const promesasMantenimientos = citasSinCache.map(async (citaId) => {
        try {
          const mantenimiento = await mantenimientosApiService.getMantenimientoPorCita(citaId);
          cargandoMantenimientos.current.delete(citaId);
          return { citaId, mantenimiento };
        } catch (error) {
          console.error(`Error cargando mantenimiento para cita ${citaId}:`, error);
          cargandoMantenimientos.current.delete(citaId);
          return { citaId, mantenimiento: null };
        }
      });
      
      // Esperar todas las solicitudes en paralelo
      const resultados = await Promise.all(promesasMantenimientos);
      
      // Actualizar estado solo con los mantenimientos válidos
      setMantenimientosData(prev => {
        const nuevosMantenimientos = { ...prev };
        resultados.forEach(({ citaId, mantenimiento }) => {
          if (mantenimiento) {
            nuevosMantenimientos[citaId] = mantenimiento;
          }
        });
        return nuevosMantenimientos;
      });
    };

    cargarMantenimientos();
  }, [citas]);
  
  // Efecto para procesar citas cuando cambian
  useEffect(() => {
    // Procesar citas cuando se actualicen
  }, [citas]);

  // Función para abrir el modal de finalizar mantenimiento
  const handleFinalizarMantenimiento = (cita) => {
    setCitaToFinalize(cita);
    setFinalizarModalOpen(true);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setFinalizarModalOpen(false);
    setCitaToFinalize(null);
  };

  // Función para manejar el éxito del mantenimiento
  const handleMantenimientoSuccess = () => {
    setFinalizarModalOpen(false);
    setCitaToFinalize(null);
    
    // Refrescar las citas
    if (onRefreshCitas) {
      onRefreshCitas();
    }
  };

  // Función para ver detalle del mantenimiento
  const handleVerDetalleMantenimiento = (cita) => {
    const citaId = cita.id || cita.ID;
    const mantenimiento = mantenimientosData[citaId];
    
    if (mantenimiento) {
      setMantenimientoSeleccionado(mantenimiento);
      setMantenimientoDetailOpen(true);
    }
  };

  // Función para marcar cita como "No Asistió"
  const handleMarcarNoAsistio = async (cita) => {
    try {
      const citaId = cita.id || cita.ID;
      
      // Actualizar el estado de la cita a "No Asistió" usando el enum
      await citasApiService.updateCita(citaId, {
        ...cita,
        Estado: ESTADOS_CITAS.NO_ASISTIO.nombre,
        estado_id: ESTADOS_CITAS.NO_ASISTIO.id
      });
      
      toastService.success(`Cita marcada como "${ESTADOS_CITAS.NO_ASISTIO.nombre}"`);
      
      // Refrescar las citas
      if (onRefreshCitas) {
        onRefreshCitas();
      }
    } catch (error) {
      console.error('Error al marcar cita como No Asistió:', error);
      toastService.error('Error al actualizar el estado de la cita');
    }
  };

  const handleFotosAntes = (cita) => {
    // Normalizar campos requeridos por FotosAntesModal (usa cita.id y cita.patente)
    const citaNormalizada = {
      ...cita,
      id: cita.id || cita.ID,
      patente: cita.patente || cita.Patente,
    };
    setFotosAntesCita(citaNormalizada);
    setFotosAntesModalOpen(true);
  };

  // Función helper para mapear estado de cita a variante de badge
  const getEstadoBadgeVariant = (estado) => {
    switch (estado) {
      case ESTADOS_CITAS.AGENDADA.nombre:
      case ESTADOS_CITAS.PENDIENTE.nombre:
        return 'warning';
      case ESTADOS_CITAS.CONFIRMADA.nombre:
        return 'primary';
      case ESTADOS_CITAS.EN_PROCESO.nombre:
        return 'info';
      case ESTADOS_CITAS.COMPLETADA.nombre:
        return 'success';
      case ESTADOS_CITAS.NO_ASISTIO.nombre:
      case ESTADOS_CITAS.CANCELADA.nombre:
        return 'danger';
      default:
        return 'primary';
    }
  };

  // Función helper para obtener los servicios y repuestos de una cita
  const getServiciosCita = (cita) => {
    const citaId = cita.id || cita.ID;
    const mantenimiento = mantenimientosData[citaId];
    const esCompletada = cita.Estado === ESTADOS_CITAS.COMPLETADA.nombre && cita.finalizada;
    
    // Si hay mantenimiento cargado, usar los servicios y repuestos reales del mantenimiento
    if (mantenimiento && mantenimiento.Servicios && mantenimiento.Servicios.length > 0) {
      return {
        servicios: mantenimiento.Servicios.map(s => ({
          Nombre: s.servicio?.nombre || 'Servicio realizado',
          Precio: s.precio || 0,
          esMantenimiento: true
        })),
        repuestos: mantenimiento.Repuestos ? mantenimiento.Repuestos.map(r => ({
          Nombre: r.repuesto?.nombre || 'Repuesto usado',
          Cantidad: r.cantidad || 1,
          PrecioUnitario: r.precio_unitario || 0,
          esMantenimiento: true
        })) : [],
        total: mantenimiento.total || 0,
        esMantenimiento: true
      };
    }
    
    // Si es una cita completada pero aún no tenemos los datos del mantenimiento, mostrar como mantenimiento en loading
    if (esCompletada) {
      return {
        servicios: [{ Nombre: 'Cargando detalles...', Precio: 0, esMantenimiento: true }],
        repuestos: [],
        total: 0,
        esMantenimiento: true,
        cargando: true
      };
    }
    
    // Si no es completada, usar servicios originales de la cita
    let servicios = null;
    if (Array.isArray(cita.ServiciosSeleccionados) && cita.ServiciosSeleccionados.length > 0) {
      servicios = cita.ServiciosSeleccionados;
    } else if (Array.isArray(cita.Servicios) && cita.Servicios.length > 0) {
      servicios = cita.Servicios;
    }
    
    return {
      servicios: servicios || [],
      repuestos: [],
      total: 0,
      esMantenimiento: false
    };
  };

  if (!selectedDate) return null;
  
  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium mb-3">
        Citas programadas para {selectedDate.format('dddd, D [de] MMMM')}
      </h3>
      
      {citas.length === 0 ? (
        <div className="text-center py-6 text-gray-400">
          No hay citas programadas para este día
        </div>
      ) : (
        <div className={`${!isMobile ? 'grid grid-cols-2 gap-4' : 'space-y-4'}`}> {/* Grid en escritorio, columna en móvil */}
          {citas.map((cita, index) => {
              // Los datos del vehículo y cliente ya vienen transformados desde la API
              const vehiculoInfo = cita.VehiculoInfo || 'Vehículo no especificado';
              const clienteNombre = cita.ClienteNombre || 'Cliente no especificado';
              const clienteTelefono = cita.ClienteTelefono || '';
              const patente = cita.Patente || 'Sin patente';
              
              // Buscar vehículo completo si es necesario (para datos adicionales)
              // const vehiculo = vehiculos.find(v => v.patente === patente);
              
              // Buscar cliente completo si es necesario (para datos adicionales)
              // const cliente = clientes.find(c => c.id === cita.ClienteID);
              
              // Determinar color según estado usando enum
              const statusColor = getColorEstadoCita(cita.Estado);
              
              return (
                <div key={index} className="bg-gray-800 rounded-lg flex flex-col border border-gray-700 hover:border-gray-600 transition-all duration-200 shadow-sm relative overflow-hidden">
                  <div className={`${statusColor} w-1 h-full absolute left-0 top-0 bottom-0 rounded-l-md`}></div>
                  
                  {/* Cuerpo de la tarjeta */}
                  <div className="flex-grow p-4">
                    {/* Primera fila: hora y estado */}
                    <div className="flex items-center h-8">
                      {isMobile ? (
                        /* Layout móvil con proporciones 5:4:1 */
                        <>
                          {/* Hora - 5 partes */}
                          <div className="flex items-center h-full flex-5">
                            <FaClock className="mr-2 text-blue-400" />
                            <span className="font-medium">{cita.Hora ? cita.Hora.substring(0, 5) : 'Sin hora'}</span>
                            {cita.HoraFin && (
                              <span className="text-gray-400 ml-2">- {cita.HoraFin.substring(0, 5)}</span>
                            )}
                          </div>
                          
                          {/* Badge - 4 partes */}
                          <div className="flex items-center justify-center h-full flex-4">
                            <Badge variant={getEstadoBadgeVariant(cita.Estado)} size="sm">
                              {cita.Estado}
                            </Badge>
                          </div>
                          
                          {/* Botón - 1 parte */}
                          <div className="flex items-center justify-center h-full flex-1">
                            {(() => {
                              const citaId = cita.id || cita.ID;
                              const mantenimiento = mantenimientosData[citaId];
                              const hasMaintenanceToView = mantenimiento && cita.Estado === ESTADOS_CITAS.COMPLETADA.nombre;
                              
                              return (
                                <AppointmentContextMenu
                                  cita={cita}
                                  isAdmin={isAdmin}
                                  isSuperAdmin={isSuperAdmin}
                                  clienteData={clienteData}
                                  hasMaintenanceToView={hasMaintenanceToView}
                                  onViewMaintenance={() => handleVerDetalleMantenimiento(cita)}
                                  onFinalizeMaintenance={() => handleFinalizarMantenimiento(cita)}
                                  onMarkNoShow={() => handleMarcarNoAsistio(cita)}
                                  onFotosAntes={() => handleFotosAntes(cita)}
                                  onEdit={() => onEditCita(cita)}
                                  onDelete={onDeleteCita ? () => onDeleteCita(cita) : null}
                                />
                              );
                            })()}
                          </div>
                        </>
                      ) : (
                        /* Layout escritorio */
                        <>
                          {/* Lado izquierdo: hora (tamaño fijo) */}
                          <div className="flex items-center h-full flex-shrink-0">
                            <FaClock className="mr-2 text-blue-400" />
                            <span className="font-medium">{cita.Hora ? cita.Hora.substring(0, 5) : 'Sin hora'}</span>
                            {cita.HoraFin && (
                              <span className="text-gray-400 ml-2">- {cita.HoraFin.substring(0, 5)}</span>
                            )}
                          </div>
                          
                          {/* Centro: motivo (con espacio flexible) */}
                          <div className="flex-1 min-w-0 mx-4">
                            <span className="font-bold truncate block" title={cita.Motivo}>
                              {cita.Motivo}
                            </span>
                          </div>
                          
                          {/* Derecha: estado y menú (tamaño fijo) */}
                          <div className="flex items-center gap-4 h-full flex-shrink-0">
                            <Badge variant={getEstadoBadgeVariant(cita.Estado)} size="sm">
                              {cita.Estado}
                            </Badge>
                            {(() => {
                              const citaId = cita.id || cita.ID;
                              const mantenimiento = mantenimientosData[citaId];
                              const hasMaintenanceToView = mantenimiento && cita.Estado === ESTADOS_CITAS.COMPLETADA.nombre;
                              
                              return (
                                <AppointmentContextMenu
                                  cita={cita}
                                  isAdmin={isAdmin}
                                  isSuperAdmin={isSuperAdmin}
                                  clienteData={clienteData}
                                  hasMaintenanceToView={hasMaintenanceToView}
                                  onViewMaintenance={() => handleVerDetalleMantenimiento(cita)}
                                  onFinalizeMaintenance={() => handleFinalizarMantenimiento(cita)}
                                  onMarkNoShow={() => handleMarcarNoAsistio(cita)}
                                  onFotosAntes={() => handleFotosAntes(cita)}
                                  onEdit={() => onEditCita(cita)}
                                  onDelete={onDeleteCita ? () => onDeleteCita(cita) : null}
                                />
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Segunda fila: motivo (solo en móvil) */}
                    {isMobile && (
                      <div className="mt-3">
                        <span className="font-bold">{cita.Motivo}</span>
                      </div>
                    )}
                    
                    {/* Tercera fila: información de vehículo y cliente */}
                    <div className="mt-3">
                      <div className="flex justify-between items-center">
                        <div className="flex flex-wrap items-center gap-2 flex-1">
                        {/* Información del vehículo */}
                        {vehiculoInfo && (
                          <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                            <FaCar className="mr-1 text-blue-400" size={12} />
                            <span className="font-medium text-sm">{vehiculoInfo}</span>
                          </div>
                        )}
                        
                        {/* Patente como elemento independiente */}
                        {patente && (
                          <div className="inline-flex items-center">
                            <PatenteDisplay patente={patente} size="xxs" />
                          </div>
                        )}
                        
                        {/* Información del cliente */}
                        {clienteNombre && (
                          <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                            <FaUserAlt className="mr-1 text-green-400" size={12} />
                            <span className="font-medium text-sm">{clienteNombre}</span>
                            {clienteTelefono && (
                              <span className="ml-1 text-xs text-gray-300">
                                {clienteTelefono}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Información del mecánico (solo para citas completadas) */}
                        {(() => {
                          const citaId = cita.id || cita.ID;
                          const mantenimiento = mantenimientosData[citaId];
                          const mecanicoNombre = mantenimiento?.Mecanicos?.[0]?.mecanicos.nombre;
                          
                          if (cita.Estado === ESTADOS_CITAS.COMPLETADA.nombre && mecanicoNombre) {
                            return (
                              <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                                <FaUserTie className="mr-1 text-purple-400" size={12} />
                                <span className="font-medium text-sm">{mecanicoNombre}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        </div>
                        

                      </div>
                      {(() => {
                        // Obtener servicios y repuestos usando la función helper
                        const { servicios, repuestos, esMantenimiento, cargando } = getServiciosCita(cita);
                        
                        // Si no hay servicios ni repuestos, mostrar un mensaje informativo
                        if ((!servicios || servicios.length === 0) && (!repuestos || repuestos.length === 0)) {
                          return (
                            <div className="mt-3">
                              <div className="flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                                <Badge variant="secondary" size="sm">
                                  <FaInfoCircle className="mr-1" size={10} />
                                  Sin servicios seleccionados
                                </Badge>
                              </div>
                            </div>
                          );
                        }
                        
                        // Función para calcular el precio total, igual que en AppointmentForm
                        // const calcularPrecioTotal = (servicios) => {
                        //   if (!servicios || !Array.isArray(servicios)) return 0;
                        //   
                        //   return servicios.reduce((total, servicio) => {
                        //     // Obtener el precio del servicio seleccionado
                        //     let precio = 0;
                        //     
                        //     // Si el servicio ya tiene un precio, usarlo directamente
                        //     if (servicio.Precio) {
                        //       precio = Number(servicio.Precio) || 0;
                        //     } else {
                        //       // Si no tiene precio, buscar el servicio en la lista completa
                        //       const servicioCompleto = servicios.find(s => s.id === servicio.id || 
                        //                                                   (servicio.Servicio && 
                        //                                                    (s.id === servicio.Servicio || 
                        //                                                     (Array.isArray(servicio.Servicio) && 
                        //                                                      servicio.Servicio.includes(s.id)))));
                        //       if (servicioCompleto && servicioCompleto.Precio) {
                        //         precio = Number(servicioCompleto.Precio) || 0;
                        //       }
                        //     }
                        //     
                        //     return total + precio;
                        //   }, 0);
                        // };
                        
                        return (
                          <div className="mt-3 space-y-2">
                            {/* Servicios */}
                            {servicios && servicios.length > 0 && (
                              <div className="flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                                {esMantenimiento ? (
                                  <FaWrench className="mr-1 text-green-400" size={12} />
                                ) : (
                                  <FaTools className="mr-1 text-yellow-400" size={12} />
                                )}
                                <span className="font-medium text-sm mr-2">
                                  {esMantenimiento ? 'Mantenimiento realizado:' : 'Servicios:'}
                                </span>
                                {/* Lista de servicios como badges */}
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {servicios.map((servicio, i) => (
                                    <Badge 
                                      key={i} 
                                      variant={cargando ? 'secondary' : (esMantenimiento ? 'success' : 'primary')} 
                                      size="sm"
                                    >
                                      {servicio.Nombre}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Repuestos */}
                            {repuestos && repuestos.length > 0 && (
                              <div className="flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                                <FaCog className="mr-1 text-orange-400" size={12} />
                                <span className="font-medium text-sm mr-2">
                                  Repuestos usados:
                                </span>
                                {/* Lista de repuestos como badges */}
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {repuestos.map((repuesto, i) => (
                                    <Badge 
                                      key={i} 
                                      variant="warning" 
                                      size="sm"
                                    >
                                      {repuesto.Nombre} ({repuesto.Cantidad})
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Cuarta fila: tiempo, servicios y precio */}
                      <div className="flex justify-between items-center mt-3">
                        {(() => {
                          // Obtener servicios y repuestos usando la función helper (igual que la primera sección)
                          const { servicios, repuestos, total, esMantenimiento, cargando } = getServiciosCita(cita);
                          
                          // Si no hay servicios ni repuestos, mostrar tiempo y mensaje informativo
                          if ((!servicios || servicios.length === 0) && (!repuestos || repuestos.length === 0)) {
                            return (
                              <>
                                <div className="flex items-center space-x-2">
                                  {cita.TiempoTotal && (
                                    <Badge variant="info" size="sm">
                                      <FaClock className="mr-1" size={10} />
                                      {cita.TiempoTotal} min
                                    </Badge>
                                  )}
                                </div>
                                <Badge variant="secondary" size="sm">
                                  <FaInfoCircle className="mr-1" size={10} />
                                  Sin servicios
                                </Badge>
                              </>
                            );
                          }
                          
                          // Función para calcular el precio total para servicios originales
                          const calcularPrecioTotal = (servicios) => {
                            if (!servicios || !Array.isArray(servicios)) return 0;
                            
                            return servicios.reduce((total, servicio) => {
                              let precio = 0;
                              if (servicio.Precio) {
                                precio = Number(servicio.Precio) || 0;
                              }
                              return total + precio;
                            }, 0);
                          };
                          
                          const totalPrecio = (esMantenimiento && !cargando) ? total : calcularPrecioTotal(servicios);
                          
                          return (
                            <>
                              <div className="flex items-center space-x-2">
                                {cita.TiempoTotal && (
                                  <Badge variant="info" size="sm">
                                    <FaClock className="mr-1" size={10} />
                                    {cita.TiempoTotal} min
                                  </Badge>
                                )}
                                <Badge variant={cargando ? 'secondary' : (esMantenimiento ? 'success' : 'primary')} size="sm">
                                  {esMantenimiento ? (
                                    <FaWrench className="mr-1" size={10} />
                                  ) : (
                                    <FaTools className="mr-1" size={10} />
                                  )}
                                  {cargando ? 'Cargando...' : `${servicios.length} ser.`}
                                </Badge>
                                {repuestos && repuestos.length > 0 && (
                                  <Badge variant="warning" size="sm">
                                    <FaCog className="mr-1" size={10} />
                                    {repuestos.length} rep.
                                  </Badge>
                                )}
                              </div>
                              <span className={`text-sm font-bold flex items-center ${
                                cargando ? 'text-gray-400' : (esMantenimiento ? 'text-green-400' : 'text-blue-400')
                              }`}>
                                <FaDollarSign className="mr-1" size={10} />
                                {cargando ? '---' : totalPrecio.toLocaleString()}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                </div>
              );
            })}
          </div>
        )}

        {/* Modal para finalizar mantenimiento */}
        <FinalizarMantenimientoModal
          isOpen={finalizarModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleMantenimientoSuccess}
          cita={citaToFinalize}
        />

        {/* Modal de detalle del mantenimiento */}
        <MantenimientoDetailModal
          isOpen={mantenimientoDetailOpen}
          onClose={() => {
            setMantenimientoDetailOpen(false);
            setMantenimientoSeleccionado(null);
          }}
          mantenimiento={mantenimientoSeleccionado}
        />

        {/* Modal de fotos antes */}
        <FotosAntesModal
          isOpen={fotosAntesModalOpen}
          onClose={() => {
            setFotosAntesModalOpen(false);
            setFotosAntesCita(null);
          }}
          cita={fotosAntesCita}
          onSuccess={() => {
            if (onRefreshCitas) onRefreshCitas();
          }}
        />
    </div>
  );
};

export default AppointmentList;